/**
 * Shared poster filter recipes — the SINGLE source of truth used by both the
 * live preview (CSS `filter` on the strip thumbnails) and the high-res canvas
 * bake (manual pixel ops), so what the customer sees is exactly what prints.
 *
 * IMPORTANT: the bake uses applyToImageData() (manual per-pixel math), NOT canvas
 * `ctx.filter`, because `ctx.filter` is unsupported on older Safari/iOS and would
 * silently bake/print an UNFILTERED photo while the CSS-filtered thumbnails looked
 * correct. The manual ops mirror the CSS recipes closely.
 *
 * Exposed as a global (these scripts are plain <script defer>, not ES modules):
 *   window.PosteraPosterFilters.order          -> string[] of filter keys in UI order
 *   window.PosteraPosterFilters.cssFilter(k)    -> CSS filter string for previews ('none' if unknown)
 *   window.PosteraPosterFilters.labelKey(k)     -> i18n key for the filter's name
 *   window.PosteraPosterFilters.applyToImageData(imageData, k) -> mutates pixels in place
 */
(function () {
  var RECIPES = {
    original:  '',
    greyscale: 'grayscale(1)',
    mono:      'grayscale(1) contrast(1.25) brightness(0.95)',
    chilly:    'hue-rotate(-10deg) saturate(1.05) brightness(1.02)',
    soft:      'contrast(0.92) brightness(1.05) saturate(0.95)',
    sunny:     'saturate(1.25) sepia(0.08) brightness(1.05) contrast(1.05)'
  };

  function clamp(v) { return v < 0 ? 0 : (v > 255 ? 255 : v); }

  // Per-pixel ops. Each mutates p = [r,g,b] in place. Math mirrors CSS/SVG filters.
  var OPS = {
    brightness: function (p, a) { p[0] *= a; p[1] *= a; p[2] *= a; },
    contrast: function (p, a) { p[0] = (p[0] - 128) * a + 128; p[1] = (p[1] - 128) * a + 128; p[2] = (p[2] - 128) * a + 128; },
    grayscale: function (p, a) { var y = 0.2126 * p[0] + 0.7152 * p[1] + 0.0722 * p[2]; p[0] += (y - p[0]) * a; p[1] += (y - p[1]) * a; p[2] += (y - p[2]) * a; },
    saturate: function (p, a) { var y = 0.2126 * p[0] + 0.7152 * p[1] + 0.0722 * p[2]; p[0] = y + (p[0] - y) * a; p[1] = y + (p[1] - y) * a; p[2] = y + (p[2] - y) * a; },
    sepia: function (p, a) {
      var r = p[0], g = p[1], b = p[2];
      var sr = 0.393 * r + 0.769 * g + 0.189 * b, sg = 0.349 * r + 0.686 * g + 0.168 * b, sb = 0.272 * r + 0.534 * g + 0.131 * b;
      p[0] = r + (sr - r) * a; p[1] = g + (sg - g) * a; p[2] = b + (sb - b) * a;
    },
    hueRotate: function (p, deg) {
      var rad = deg * Math.PI / 180, c = Math.cos(rad), s = Math.sin(rad);
      var r = p[0], g = p[1], b = p[2];
      p[0] = r * (0.213 + c * 0.787 - s * 0.213) + g * (0.715 - c * 0.715 - s * 0.715) + b * (0.072 - c * 0.072 + s * 0.928);
      p[1] = r * (0.213 - c * 0.213 + s * 0.143) + g * (0.715 + c * 0.285 + s * 0.140) + b * (0.072 - c * 0.072 - s * 0.283);
      p[2] = r * (0.213 - c * 0.213 - s * 0.787) + g * (0.715 - c * 0.715 + s * 0.715) + b * (0.072 + c * 0.928 + s * 0.072);
    }
  };

  // Op sequences in the SAME order as the CSS recipes above.
  var SEQ = {
    original:  [],
    greyscale: [['grayscale', 1]],
    mono:      [['grayscale', 1], ['contrast', 1.25], ['brightness', 0.95]],
    chilly:    [['hueRotate', -10], ['saturate', 1.05], ['brightness', 1.02]],
    soft:      [['contrast', 0.92], ['brightness', 1.05], ['saturate', 0.95]],
    sunny:     [['saturate', 1.25], ['sepia', 0.08], ['brightness', 1.05], ['contrast', 1.05]]
  };

  function applyToImageData(imageData, key) {
    var seq = SEQ[key];
    if (!seq || !seq.length) return imageData;
    var d = imageData.data, p = [0, 0, 0], i, j;
    for (i = 0; i < d.length; i += 4) {
      p[0] = d[i]; p[1] = d[i + 1]; p[2] = d[i + 2];
      for (j = 0; j < seq.length; j++) OPS[seq[j][0]](p, seq[j][1]);
      d[i] = clamp(p[0]); d[i + 1] = clamp(p[1]); d[i + 2] = clamp(p[2]);
    }
    return imageData;
  }

  window.PosteraPosterFilters = {
    order: ['original', 'chilly', 'greyscale', 'soft', 'mono', 'sunny'],
    recipes: RECIPES,
    cssFilter: function (key) {
      var r = RECIPES[key];
      return (r === undefined || r === '') ? 'none' : r;
    },
    labelKey: function (key) { return 'custom_photo.filter_' + key; },
    applyToImageData: applyToImageData
  };
})();
